import { effect, inject, Injectable, signal } from "@angular/core";

import { Observable, firstValueFrom, map } from "rxjs";
import { catchError } from "rxjs/operators";
import { ApiClient } from "./api-client";
import {
  GenericApiResponse,
  GenericResponse,
} from "../shared/common/api-response.model";
import OktaAuth, { TokenManager } from "@okta/okta-auth-js";
import { OKTA_AUTH } from "@okta/okta-angular";
import { Router } from "@angular/router";
import { AppRoutePaths, AppRoutes } from "../shared/common/app-path.routes";
import { GlobalServiceService } from "../shared/services/global-service.service";
import {
  AccessLevel,
  Login,
  LoginResponse,
  UserResult,
} from "../shared/models/login.model";
import { InternalUserRegistration, UserDto } from "../shared/models/user.model";
import { environment } from "../environments/environment.development";
import { LogsModel, SessionLogsModel } from "../shared/models/log.model";
import { HttpErrorResponse, HttpRequest } from "@angular/common/http";
import { LoaderService } from "src/app/core/Loader.services";
import {
  CompanyDto,
  PagedResult,
  ApproverDto,
  ExternalUserRegistration,
  ApiResponse,
} from "src/app/shared/models/registration.models";
import { LobbyApps } from "./admin.service";
import {
  NotificationPlacement,
  PriorityEnum,
} from "../shared/common/common.enum";
import { NotificationService } from "./notification.service";

export interface RegistrationPrefill {
  email: string;
  displayName?: string;
  department?: string;
  roles?: string[];
}
@Injectable({ providedIn: "root" })
export class UsersService {
  private okta = inject<OktaAuth>(OKTA_AUTH);
  private router = inject(Router);
  private gbService = inject(GlobalServiceService);
  private notification = inject(NotificationService);
  constructor(
    private api: ApiClient,
    private loader: LoaderService,
  ) {
    effect(() => {
      const msg = this.LoginErrorMsg();
      if (msg) {
        sessionStorage.setItem("LoginErrorMsg", msg);
      } else {
        sessionStorage.removeItem("LoginErrorMsg");
      }
    });
  }
  LoginErrorMsg = signal<string | null>(
    sessionStorage.getItem("LoginErrorMsg") ?? "",
  );
  list(): Observable<UserDto[]> {
    return this.api.get<UserDto[]>("/users");
  }
  me(): Observable<UserDto> {
    return this.api.get<UserDto>("/me");
  }

  login(login: Login): Observable<LoginResponse> {
    return this.api.post("Auth/UserLogin", login);
  }

  getAppUrl(
    appId: number,
    type: string,
  ): Observable<GenericResponse<LobbyApps>> {
    return this.api.get<GenericResponse<LobbyApps>>(
      `Home/GetAppUrl?AppId=${appId}&Type=${type}`,
    );
  }

  testAPICall(): Observable<GenericApiResponse<string>> {
    return this.api.get<GenericApiResponse<string>>("Home/TestHomeAPI");
  }

  // logError(payload: LogsModel): Observable<any> {
  //   return this.api.post("Home/InsertLobbyLogs", payload);
  // }
  UpdateSessionInfo(payload: SessionLogsModel): Observable<any> {
    return this.api.post("Home/InsertLobbySessionLogs", payload);
  }
  // logAppAccess(payload: LogsModel): Observable<any> {
  //   return this.api.post("Home/InsertLobbyLogs", payload);
  // }
  // getExternalUserFormSeed(
  // ): Observable<string> {
  //   return this.api.get<string>(
  //     `User/GetSeed`
  //   );
  // }
  private loginInProgress = false; // prevent re-entrant calls
  private loginAttempts = 0;
  private readonly MAX_LOGIN_ATTEMPTS = 3;

  async startLogin(): Promise<UserResult | null> {
    
    // :white_check_mark: Guard: prevent loop / re-entrant calls
    if (this.loginInProgress) {
      //console.warn("Login already in progress. Skipping duplicate call.");
      return null;
    } // :white_check_mark: Guard: max retry attempts to break infinite loops

    if (this.loginAttempts >= this.MAX_LOGIN_ATTEMPTS) {
      this.LoginErrorMsg.set(
        "Login failed after multiple attempts. Please refresh and try again after some time.",
      );
      this.loginAttempts = 0;
      this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
      return null;
    }

    this.loginInProgress = true;
    this.loginAttempts++;

    try {
      // :white_check_mark: Wrap Okta calls individually to pinpoint which one fails
      let userEmail: string;
      try {
        userEmail = (await this.okta.getUser()).email ?? "";
      } catch (oktaUserError) {
        console.error("Okta getUser() failed:", oktaUserError);
        this.LoginErrorMsg.set(
          "Login failed. Unable to reach Okta for user info. Please try again.",
        );
        this.okta.signOut(); // Clear any partial state in Okta
        this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
        return null;
      }

      if (!userEmail) {
        this.okta.signOut(); // Clear any partial state in Okta
        this.LoginErrorMsg.set(
          "Login failed. Unable to retrieve user email from Okta.",
        );

        this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
        return null;
      } // :white_check_mark: Wrap token fetches — these can fail independently

      let idToken: string;
      let OKTAaccessToken: string;
      try {
        idToken = (await this.okta.getIdToken()) ?? "";
        OKTAaccessToken = (await this.okta.getAccessToken()) ?? "";
      } catch (tokenError) {
        console.error("Okta token fetch failed:", tokenError);
        this.LoginErrorMsg.set(
          "Login failed. Could not retrieve authentication tokens. Please sign in again.",
        );
        this.okta.signOut(); // Clear any partial state in Okta
        this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
        return null;
      } // :white_check_mark: Validate tokens before sending to API

      if (!idToken || !OKTAaccessToken) {
        this.LoginErrorMsg.set(
          "Login failed. Authentication tokens are missing or expired.",
        );
        this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
        return null;
      }

      const loginModel: Login = {
        ApplicationName: environment.applicationName,
        Is_Okta: true,
        UserName: userEmail,
        RefreshToken: OKTAaccessToken,
        IdToken: idToken,
        Password: "",
      } as Login;

      this.LoginErrorMsg.set("");
      this.setUserEmailInSession(userEmail);
      this.notification.removeDashboardNotify(); // :white_check_mark: Wrap the API call separately

      let userInfo: UserResult | null;
      try {
        userInfo = await this.loginRequest(loginModel);
      } catch (apiError: any) {
        //console.error("Login API request failed:", apiError); // :white_check_mark: Handle specific HTTP error codes

        const status = apiError?.status ?? apiError?.statusCode;
        if (status === 401) {
          this.LoginErrorMsg.set(
            "Unauthorized. Your session may have expired.",
          );
        } else if (status === 403) {
          this.LoginErrorMsg.set(
            "Access denied. You don't have permission to log in.",
          );
        } else if (status === 0 || status === 503) {
          this.LoginErrorMsg.set(
            "Server unreachable. Please check your connection.",
          );
        } else {
          this.LoginErrorMsg.set(
            "Login failed. Unable to retrieve user email from Okta.",
          );
        }
        this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
        return null;
      } // :white_check_mark: Handle null/empty response from API

      if (!userInfo) {
        this.LoginErrorMsg.set(
          "Login failed. No user data returned from the server.",
        );
        this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
        return null;
      } // :white_check_mark: Success — reset counters

      this.loginAttempts = 0;
      this.setUserTypelInSession(userInfo["user-type"] ?? "");
      this.setUserIdInSession(userInfo?.["user-id"]?.toString() ?? "");
      return userInfo;
    } finally {
      // :white_check_mark: Always release the lock, even if something throws unexpectedly
      this.loginInProgress = false;
    }
  }

  async startLogin_OLD(): Promise<UserResult | null> {
    try {
      let userEmail = (await this.okta.getUser()).email ?? "";
      let idToken = await this.okta.getIdToken();
      let OKTAaccessToken = await this.okta.getAccessToken();

      for (let attempts = 0; attempts < 3; attempts++) {
        if (!userEmail) {
          await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait for 1 second
          userEmail = (await this.okta.getUser()).email ?? "";
        }
        if (!idToken) {
          await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait for 1 second
          idToken = await this.okta.getIdToken();
        }
        if (!OKTAaccessToken) {
          await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait for 1 second
          OKTAaccessToken = await this.okta.getAccessToken();
        }
        if (userEmail && idToken && OKTAaccessToken) break;
      }

      if (userEmail && idToken && OKTAaccessToken) {
        const loginModel: Login = {
          ApplicationName: environment.applicationName,
          Is_Okta: true,
          UserName: userEmail,
          RefreshToken: OKTAaccessToken,
          IdToken: idToken,
          Password: "",
        } as Login;
        this.LoginErrorMsg.set("");
        this.setUserEmailInSession(userEmail);

        this.notification.removeDashboardNotify();
        const userInfo = await this.loginRequest(loginModel);
        this.setUserTypelInSession(userInfo ? userInfo["user-type"] : "");
        return userInfo;
      } else {
        this.LoginErrorMsg.set(
          "Login failed. Unable to retrieve required tokens or user email from Okta after multiple attempts.",
        );
        this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
        return null;
      }
    } catch (error) {
      this.LoginErrorMsg.set(
        "Login failed. Okta API call failed due to threshold limit or other issues.",
      );
      this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
      return null;
    }
  }

  async loginRequest(loginModel: Login): Promise<UserResult | null> {
    const response = await firstValueFrom(
      this.login(loginModel).pipe(
        catchError((err) => {
          throw err;
        }),
      ),
    );
    if (!response || !response.Token?.AccessToken || !response.Result?.length) {
      //alert('Login failed. Please check your credentials.');
      // this.getNotificationForLogin();
      this.LoginErrorMsg.set(
        `Login failed. Please check your OKTA authentication ! 
        For new CP Central account, please click the 'Register' button and complete the form, we will process your registration in the order in which it was received.`,
      );
      //catch error logging here
      const logModal2: LogsModel = {
        category: "Error",
        correlationId: this.getUserEmailFromSession() ?? "",
        eventId: "Login Authencation Failure",
        ipAddress: "",
        message: `Login failed for ${loginModel.UserName} Issue with API result.`,
        payloadJson: response
          ? (JSON.stringify(response.Result) ?? response.error ?? "")
          : "",
        source: "Login Module",
        stackTrace: "",
        userId: this.gbService.userData()?.["user-id"]?.toString() ?? "",
      } as LogsModel;
      // this.errorLogger(
      //   new HttpErrorResponse({
      //     //error: "Login failed. You don't have access to CP Central.",
      //     error: {
      //       message: `Login failed for ${loginModel.UserName} You don't have access to CP Central.`,
      //     },
      //     status: 400,
      //     statusText: "Bad Request",
      //     url: "Login",
      //   }),
      //   new HttpRequest("GET", "Login")
      // );

      this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login], {
        state: {
          loginError: `Login failed. You don't have access to CP Central. Please submit registration form to get access.`,
        },
      });
      return null;
    }
    const userInfo: UserResult = response.Result[0];
    this.gbService.setCPToken(response.Token.AccessToken);

    if (!userInfo["is-active"]) {
      // this.getNotificationForLogin();

      //alert("Login failed,Inactive user Please check with CP central Admin.");
      this.LoginErrorMsg.set(
        `Login failed. You account is inacitve. Please contact CP Central support at central@nick.com`,
      );
      // this.errorLogger(
      //   new HttpErrorResponse({
      //     //error: "Login failed. You account is inacitve.",
      //     error: { message: `${loginModel.UserName} inactive account access` },
      //     status: 400,
      //     statusText: "Bad Request",
      //     url: "Login",
      //   }),
      //   new HttpRequest("GET", "Login")
      // );

      this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
      return null;
    }
    if (
      userInfo["access-level"].length === 0 ||
      userInfo["access-level"] === null
    ) {
      this.LoginErrorMsg.set(
        "Login failed, you dont have active app access, please check with CP central support at central@nick.com",
      );
      // this.errorLogger(
      //   new HttpErrorResponse({
      //     error: {
      //       message: `${loginModel.UserName} No active app access level`,
      //     },
      //     status: 400,
      //     statusText: "Bad Request",
      //     url: "Login",
      //   }),
      //   new HttpRequest("GET", "Login")
      // );

      this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
      return null;
    }
    //check for active access level
    if (
      !userInfo["access-level"].some(
        (access: AccessLevel) => access["is-active"] === true,
      )
    ) {
      // this.getNotificationForLogin();

      this.LoginErrorMsg.set(
        "Login failed, you dont have active app access, please check with CP central support at central@nick.com",
      );
      // this.errorLogger(
      //   new HttpErrorResponse({
      //     error: {
      //       message: `${loginModel.UserName} No active app access level`,
      //     },
      //     status: 400,
      //     statusText: "Bad Request",
      //     url: "Login",
      //   }),
      //   new HttpRequest("GET", "Login")
      // );

      this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
      return null;
    }
    //await this.updateUserInfo(userInfo);
    // Enrich with per-app URLs in parallel
    await this.updateUserInfo(userInfo);

    //await this.InsertSessioforuser(userInfo);

    // Signal: user data is hydrated in memory
    this.gbService.setUserInfo(userInfo);

    // NOTE: Do NOT navigate here in refresh/hydrate path unless desired.
    // If you want automatic navigation after interactive login only, keep it in your login flow.

    return userInfo;
    //this.login(loginModel).subscribe(async (response: LoginResponse) => {
    // if (
    //   response &&
    //   response.Token.AccessToken &&
    //   response.Result.length > 0
    // )
    // {

    // } else {
    //   alert("Login failed. Please check your credentials.");
    //   this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
    //   return null;
    // }
    // });
  }

  async updateUserInfo(userInfo: UserResult): Promise<void> {
    //const userType = userInfo["user-type"];
    //Push dealt access 'Support Center'

    this.getSupportCenterApplication(userInfo);

    //userInfo["access-level"]=...userInfo["access-level"].filter() =>
    const promises = userInfo["access-level"].map((access: AccessLevel) => {
      if (access["is-active"] === true) {
        return this.getAppUrl(access["application-id"], userInfo["user-type"])
          .toPromise() // Convert Observable to Promise
          .then((res: GenericResponse<LobbyApps> | undefined) => {
            if (res && res.status && res.Result) {
              access["app-Url"] = res.Result?.OktaSsoUrl;
              access["placement"] = res.Result?.Placement;
              access["is-active"] = res.Result?.IsActive;
              access["application-desc"] = res.Result?.AppName;
            } else {
              access["is-active"] = false;
            }
          });
      }
    });

    await Promise.all(promises); // Wait for all requests to finish
    // userInfo["access-level"] = userInfo["access-level"].filter(
    //   (x) => x["is-active"]
    // ) as AccessLevel[];

    userInfo["access-level"] = [
      ...userInfo["access-level"].filter((access: AccessLevel) => {
        if (access["is-active"]) {
          return access;
        }
      }),
    ];

    this.getNotificationAfterLogin(userInfo["user-type"]);

    this.gbService.setUserInfo(userInfo); // Execute after all updates
    this.loader.hide();
    // const value = sessionStorage.getItem("community");
    // if (value) {
    //   // alert(value);
    //   // this.router.navigateByUrl("www.google.com");
    //   window.location.href = environment.communityRedirectUrl;
    // } else {
    // }
    this.router.navigateByUrl(AppRoutePaths[AppRoutes.Dashboard]);
  }

  private getSupportCenterApplication(userInfo: UserResult) {
    userInfo["access-level"].push({
      ["application-id"]: 0,
      ["application-desc"]: "Support Center",
      ["placement"]: 6,
      ["is-active"]: true,
    } as AccessLevel);
  }

  // errorLogger(error: HttpErrorResponse, req: HttpRequest<unknown>) {
  //   const logModal: LogsModel = {
  //     category: "Error",
  //     correlationId: this.getUserEmailFromSession() ?? "",
  //     eventId: "Login Authencation",
  //     ipAddress: "",
  //     message: error.error.message ?? error.message,
  //     payloadJson: req?.body ?? "",
  //     source: "Login Module",
  //     stackTrace: "",
  //     userId: this.gbService.userData()?.["user-id"]?.toString() ?? "",
  //   } as LogsModel;
  //   this.logError(logModal).subscribe({
  //     next: (res) => {},
  //   });
  // }

  // -----------------------
  // Session helpers for email (non-sensitive)
  // -----------------------

  private readonly USER_EMAIL_KEY = "UserEmail";

  private readonly USER_TYPE_KEY = "UserType";

  private readonly USER_ID_KEY = "UserId";

  public getUserEmailFromSession(): string {
    return sessionStorage.getItem(this.USER_EMAIL_KEY) ?? "";
  }

  private setUserEmailInSession(email: string): void {
    sessionStorage.setItem(this.USER_EMAIL_KEY, email);
  }

  public getUserTypeFromSession(): string {
    return sessionStorage.getItem(this.USER_TYPE_KEY) ?? "";
  }

  public getUserIdFromSession(): string {
    return sessionStorage.getItem(this.USER_ID_KEY) ?? "";
  }
  public setUserIdInSession(userId: string): void {
    sessionStorage.setItem(this.USER_ID_KEY, userId);
  }

  private setUserTypelInSession(userType: string): void {
    sessionStorage.setItem(this.USER_TYPE_KEY, userType);
  }

  public InsertSessioforuser(user: UserResult): void {
    const sessionModal: SessionLogsModel = {
      ipAddress: "",
      sessioninfo: this.gbService.getCPToken() ?? "",
      // navigator.userAgent,
      userId: user["user-id"]?.toString() ?? "",
      username: user.username ?? "",
      usertype: user["user-type"] ?? "",
    } as SessionLogsModel;
    this.UpdateSessionInfo(sessionModal).subscribe({
      next: (res) => {},
    });
  }

  clearUserEmailFromSession(): void {
    sessionStorage.removeItem(this.USER_EMAIL_KEY);
    sessionStorage.removeItem(this.USER_TYPE_KEY);
    sessionStorage.removeItem(this.USER_ID_KEY);
  }

  // -----------------------
  // Refresh / Hydration flow
  // -----------------------

  /**
   * Ensure user data (signal) is present. If missing (e.g., after hard refresh),
   * use startLogin() to rehydrate from the server.
   *
   * Call this from your CanMatch guard:
   *  await usersService.ensureHydrated();
   */
  async ensureHydrated(): Promise<UserResult | null> {
    const current = this.gbService.userData();
    if (current) return current; // already hydrated

    return await this.startLoginForRefresh();
  }

  /**
   * Refresh path that uses email from sessionStorage if available,
   * otherwise pulls from Okta and caches email in session.
   */
  async startLoginForRefresh(): Promise<UserResult | null> {
    // ✅ Guard: prevent re-entrant refresh calls
    if (this.loginInProgress) {
      console.warn("Login already in progress. Skipping duplicate call.");
      return null;
    }

    // ✅ Guard: break infinite refresh loops
    if (this.loginAttempts >= this.MAX_LOGIN_ATTEMPTS) {
      console.error("Max login attempts reached. Redirecting to login.");
      this.loginAttempts = 0;
      this.gbService.setUserInfo(null as any);
      this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
      return null;
    }

    this.loginInProgress = true;
    this.loginAttempts++;

    try {
      // ✅ Step 1: Resolve user email — session first, then Okta
      let userEmail: string = "";

      const fromSession = this.getUserEmailFromSession();
      if (fromSession) {
        userEmail = fromSession;
      } else {
        try {
          userEmail = (await this.okta.getUser())?.email ?? "";
        } catch (oktaUserError) {
          console.error("Okta getUser() failed during refresh:", oktaUserError);
        }
      }

      if (!userEmail) {
        console.warn(
          "No user email available for refresh. Redirecting to login.",
        );
        this.gbService.setUserInfo(null as any);
        this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
        return null;
      }

      // ✅ Step 2: Fetch tokens with isolated error handling
      let idToken: string;
      let OKTAaccessToken: string;
      try {
        idToken = (await this.okta.getIdToken()) ?? "";
        OKTAaccessToken = (await this.okta.getAccessToken()) ?? "";
      } catch (tokenError) {
        console.error("Okta token fetch failed during refresh:", tokenError);
        this.gbService.setUserInfo(null as any);
        this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
        return null;
      }

      // ✅ Step 3: Validate tokens before hitting the API
      if (!idToken || !OKTAaccessToken) {
        console.warn(
          "Tokens missing or expired during refresh. Redirecting to login.",
        );
        this.gbService.setUserInfo(null as any);
        this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
        return null;
      }

      const loginModel: Login = {
        ApplicationName: environment.applicationName,
        Is_Okta: true,
        UserName: userEmail,
        RefreshToken: OKTAaccessToken,
        IdToken: idToken,
        Password: "",
      } as Login;

      // ✅ Step 4: Call login API with specific error handling
      let userInfo: UserResult | null;
      try {
        userInfo = await this.loginRequest(loginModel);
      } catch (apiError: any) {
        console.error("Login API failed during refresh:", apiError);

        // const status = apiError?.status ?? apiError?.statusCode;
        // if (status === 401 || status === 403) {
        //   console.warn(
        //     `Auth error (${status}) during refresh. Clearing session.`,
        //   );
        //   this.gbService.setUserInfo(null as any);
        // } else {
        //   console.warn(
        //     "Non-auth error during refresh. Keeping session intact.",
        //   );
        // }

        this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
        return null;
      }

      // ✅ Step 5: Validate API response
      if (!userInfo) {
        console.warn(
          "Refresh login returned no user info. Redirecting to login.",
        );
        this.gbService.setUserInfo(null as any);
        this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
        return null;
      }

      // ✅ Success — persist session data and reset counters
      this.loginAttempts = 0;
      this.setUserEmailInSession(userEmail);
      this.setUserIdInSession(userInfo?.["user-id"]?.toString() ?? "");
      this.setUserTypelInSession(userInfo["user-type"] ?? "");
      return userInfo;
    } finally {
      // ✅ Always release lock regardless of outcome
      this.loginInProgress = false;
    }
  }
  // async startLoginForRefresh(): Promise<UserResult | null> {
  //   try {
  //     const fromSession = this.getUserEmailFromSession();
  //     const oktaProfile = fromSession ? null : await this.okta.getUser();
  //     const userEmail = fromSession || oktaProfile?.email || "";

  //     if (!userEmail) {
  //       // No way to refresh → route to login
  //       //this.gbService.clearCPToken();
  //       this.gbService.setUserInfo(null as any);
  //       this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
  //       return null;
  //     }

  //     // Build login model to hydrate server-side user state
  //     const idToken = await this.okta.getIdToken();
  //     const OKTAaccessToken = await this.okta.getAccessToken();
  //     const loginModel: Login = {
  //       ApplicationName: environment.applicationName,
  //       Is_Okta: true,
  //       UserName: userEmail,
  //       RefreshToken: OKTAaccessToken, //(this.okta.getRefreshToken() ?? "") as string,
  //       IdToken: idToken ?? "",
  //       Password: "",
  //     } as Login;

  //     // Perform login + update user info + navigate (if caller wants to stay)
  //     const userInfo = await this.loginRequest(loginModel);

  //     // Cache email for subsequent refreshes (non-sensitive; sessionStorage only)
  //     this.setUserEmailInSession(userEmail);
  //     this.setUserTypelInSession(userInfo ? userInfo["user-type"] : "");
  //     return userInfo;
  //   } catch {
  //     // On any error, send user to login
  //     this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
  //     return null;
  //   }
  // }

  // async startLoginForRefresh(): Promise<UserResult | null> {
  //   debugger;
  //   try {
  //     const fromSession = this.getUserEmailFromSession();
  //     let userEmail = fromSession || "";
  //     let idToken;
  //     let OKTAaccessToken;

  //     if (!fromSession) {
  //       for (let attempts = 0; attempts < 3; attempts++) {
  //         const oktaProfile = await this.okta.getUser();
  //         userEmail = oktaProfile?.email || "";

  //         if (!idToken) {
  //           idToken = await this.okta.getIdToken();
  //         }

  //         if (!OKTAaccessToken) {
  //           OKTAaccessToken = await this.okta.getAccessToken();
  //         }

  //         if (userEmail && idToken && OKTAaccessToken) {
  //           break;
  //         }

  //         await new Promise((resolve) => setTimeout(resolve, 1000)); // Wait for 1 second
  //       }
  //     }

  //     if (!userEmail || !idToken || !OKTAaccessToken) {
  //       // No way to refresh → route to login
  //       this.gbService.setUserInfo(null as any);
  //       this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
  //       return null;
  //     }

  //     // Build login model to hydrate server-side user state
  //     const loginModel: Login = {
  //       ApplicationName: environment.applicationName,
  //       Is_Okta: true,
  //       UserName: userEmail,
  //       RefreshToken: OKTAaccessToken,
  //       IdToken: idToken,
  //       Password: "",
  //     } as Login;

  //     // Perform login + update user info + navigate (if caller wants to stay)
  //     const userInfo = await this.loginRequest(loginModel);

  //     // Cache email for subsequent refreshes (non-sensitive; sessionStorage only)
  //     this.setUserEmailInSession(userEmail);
  //     this.setUserTypelInSession(userInfo ? userInfo["user-type"] : "");
  //     return userInfo;
  //   } catch {
  //     // On any error, send user to login
  //     this.router.navigateByUrl(AppRoutePaths[AppRoutes.Login]);
  //     return null;
  //   }
  // }

  //Section External User Registration Prefill & Save

  // Profile

  // Companies (internal search + paging)
  searchCompanies(
    query: string,
    page = 1,
    pageSize = 10,
  ): Observable<PagedResult<CompanyDto>> {
    const params = {
      q: query,
      page: page.toString(),
      pageSize: pageSize.toString(),
    }; // Ensure values are strings

    return this.api.get<PagedResult<CompanyDto>>(
      `User/GetExternalRegistrationAccount`,
    );
  }

  getCompaniesByEmail(email: string) {
    return this.api
      .get<ApiResponse<CompanyDto[]>>(`User/GetExternalRegistrationAccount`)
      .pipe(
        map((res) => {
          // validate the wrapper
          if (!res || res.status !== true) {
            // include message for debugging/UI
            throw new Error(res?.message ?? "Failed to fetch companies");
          }
          // ensure array fallback
          return Array.isArray(res.data) ? res.data : [];
        }),
      );
  }

  // External limited companies (<= 6)
  listExternalCompanies(): Observable<CompanyDto[]> {
    return this.api.get<CompanyDto[]>("companies/external");
  }
  async externalCompanies(): Promise<CompanyDto[]> {
    return await firstValueFrom(this.listExternalCompanies());
  }

  // Approvers search + paging
  // searchApprovers(query: string, page = 1, pageSize = 100): Observable<PagedResult<ApproverDto>> {
  //   return this.api.get<PagedResult<ApproverDto>>('User/GetAEList', );
  // }
  searchApprovers(query: string, page = 1, pageSize = 100) {
    return this.api.get<ApiResponse<ApproverDto[]>>(`User/GetAEList`).pipe(
      map((res) => {
        // validate the wrapper
        if (!res || res.status != true) {
          // include message for debugging/UI
          throw new Error(res?.message ?? "Failed to fetch Approvers");
        }
        // ensure array fallback
        return Array.isArray(res.data) ? res.data : [];
      }),
    );
  }
  // Email duplicate
  isEmailTaken(
    email: string,
  ): Observable<{ exists: boolean; message?: string }> {
    return this.api
      .get<{
        status: boolean;
        message: string;
      }>(`User/ValidateEmailAddress?email=${email}`)
      .pipe(
        map((res) => {
          return { exists: res.status, message: res.message };
        }),
      );
  }

  // Save
  saveRegistration(
    payload: ExternalUserRegistration,
  ): Observable<ApiResponse<any>> {
    return this.api
      .post<ApiResponse<any>>("User/SubmitExternalRegistration", payload)
      .pipe(
        map((res) => {
          if (!res || res.status != true) {
            res.message = res.data
              ? res.data[0].toString()
              : "Failed to save registration";
          }
          return res;
        }),
      );
  }
  async save(payload: ExternalUserRegistration) {
    return await firstValueFrom(this.saveRegistration(payload));
  }
  // End of External User Registration Prefill & Save

  internalUserRegistration(
    payload: InternalUserRegistration,
  ): Observable<ApiResponse<InternalUserRegistration>> {
    return this.api.post(`User/InternalUserRegistration`, payload);
  }

  async getNotificationAfterLogin(audience: string) {
    try {
      const res = await firstValueFrom(
        this.notification.getNotification(
          audience,
          NotificationPlacement.dashboard,
        ),
      );
      if (res.status && res.Result.length > 0) {
        this.notification.clear();
        const closedNotify = this.notification.getClosedNotification();
        closedNotify.forEach((closedId: string) => {
          res.Result.splice(
            res.Result.findIndex(
              (x) => x.NotificationId.toString() == closedId,
            ),
            1,
          );
        });
        res.Result.map((ele) => {
          this.notification.push({
            message: ele.Message,
            priority: ele.Priority as PriorityEnum,
            id: ele.NotificationId.toString(),
            title: ele.Title,
          });
        });
        // this.notification.persist();
      }
    } catch (err) {}
  }

  async getNotificationForLogin() {
    try {
      const res = await firstValueFrom(
        this.notification.getNotificationForLogin(),
      );
      if (res.status && res.Result.length > 0) {
        this.notification.loginClear();
        res.Result.map((ele) => {
          this.notification.pushLoginNotification({
            message: ele.Message,
            priority: ele.Priority as PriorityEnum,
            title: ele.Title,
          });
        });
      }
    } catch (err) {}
  }
  private cachedEmail: string | null = null;
  private emailPromise: Promise<string> | null = null;

getEmailFromBrowserSession(): Promise<string> {
  
  // :white_check_mark: Return cached value
  if (this.cachedEmail) {
    return Promise.resolve(this.cachedEmail);
  }
 
  // :white_check_mark: Prevent multiple calls
  if (this.emailPromise) {
    return this.emailPromise;
  }
 
  // :white_check_mark: Single execution
  this.emailPromise = this.okta.getUser()
    .then(user => {
      const email = user?.email;
 
      if (email && typeof email === 'string') {
        this.cachedEmail = email;
        return email;
      }
 
      console.warn('Email claim not found');
      return '';
    })
    .catch(error => {
      console.error('Error fetching user from Okta:', error);
      return '';
    })
    .finally(() => {
      // :white_check_mark: Allow retry in future
      this.emailPromise = null;
    });
 
  return this.emailPromise;
 }

}
